/**
 * PF-4 (fieldiq-api#1227) — the display rules for the rate card and field
 * tickets. Pure functions; the screens import these so a refusal, a status hue
 * and who may press what read the same everywhere.
 *
 * THE API DECIDES. Nothing here prices a line or adds up a ticket: the line
 * amount, the draft subtotal, the tax and the total are the API's figures. The
 * role lists mirror fieldiq-api lib/pf-roles.ts for hiding buttons only; a 403
 * that still arrives is said as a refusal.
 */
import { pfApiError, pfDay, pfRefusal, PF_UNKNOWN_SKIN, type PfSkin } from './pipeline-facility';

// ─── Who may press what (mirrors PF_TICKET_READ_ROLES / PF_TICKET_WRITE_ROLES) ─

export const PF_TICKET_READ_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher'] as const;
export const PF_TICKET_WRITE_ROLES = ['admin', 'office_staff'] as const;

const inList = (list: readonly string[]) => (role: string | null | undefined) => !!role && list.includes(role);
/** The rate card and field tickets carry prices: the office, the supervisor and dispatch read them. */
export const canReadPfTickets = inList(PF_TICKET_READ_ROLES);
/** Writing the card, drafting, editing, approving, sending, recording an approval — the office. */
export const canWritePfTickets = inList(PF_TICKET_WRITE_ROLES);

// ─── Status ──────────────────────────────────────────────────────────────────

/** Draft slate, office-approved blue (waiting on the customer), customer-approved green, rejected red. */
export const PF_TICKET_STATUS_SKIN: Record<string, PfSkin> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  office_approved: { label: 'Office approved', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  customer_approved: { label: 'Customer approved', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  customer_rejected: { label: 'Customer rejected', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};
export const pfTicketStatusSkin = (status: string | null | undefined): PfSkin =>
  status && Object.prototype.hasOwnProperty.call(PF_TICKET_STATUS_SKIN, status) ? PF_TICKET_STATUS_SKIN[status] : PF_UNKNOWN_SKIN;

/** Statuses carrying the office's snapshot (totals, tax, the PDF). */
export const PF_TICKET_SNAPSHOT = ['office_approved', 'customer_approved', 'customer_rejected'];
export const pfTicketHasSnapshot = (status: string | null | undefined) => !!status && PF_TICKET_SNAPSHOT.includes(status);

/**
 * WHICH TICKETS MAY BE EDITED, AND HOW.
 *
 *   draft              lines and header edit freely
 *   office_approved    an edit RETURNS IT TO DRAFT (the office approval, the
 *   customer_rejected  review link and the totals are withdrawn) — so the
 *                      screen asks first, and says so when the API reports
 *                      `reverted_to_draft`
 *   customer_approved  frozen; only the internal notes change
 */
export type PfTicketEditMode = 'free' | 'reverts' | 'frozen';
export function pfTicketEditMode(status: string | null | undefined): PfTicketEditMode {
  if (status === 'draft') return 'free';
  if (status === 'office_approved' || status === 'customer_rejected') return 'reverts';
  return 'frozen';
}

/** The sentence a `reverted_to_draft: true` echo is said with. Never in a success style. */
export const PF_REVERTED_NOTICE =
  'This change returned the ticket to draft. The office approval was withdrawn, and any review link sent to the customer no longer works. Approve it again before sending it.';

/** What a refresh from the daily logs does to the lines — said BEFORE the office presses it. */
export const PF_REFRESH_WARNING =
  'Refreshing redraws every line drafted from the daily logs. A price the office set on one of those lines is lost; lines the office added are kept.';

export function pfScopeText(t: { scope: string; workDate: string | null }): string {
  if (t.scope === 'day') return t.workDate ? pfDay(t.workDate) : 'One day';
  if (t.scope === 'job') return 'Whole job';
  return '—';
}

export const PF_APPROVAL_SOURCE_TEXT: Record<string, string> = {
  link: 'signed by the company man on the review link',
  office_upload: 'recorded by the office from the photo of the signed paper ticket / LEM',
};

// ─── Refusals ────────────────────────────────────────────────────────────────

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

/**
 * Every refusal code the PF-4 routes send, as one plain sentence. A code this
 * map lacks falls back to pfRefusal (the PF-2 map, then the API's own `error`).
 */
const PF_TICKET_REFUSALS: Record<string, (x: Record<string, unknown>) => string> = {
  // rate card
  rate_overlap: () =>
    'Another rate with this code already covers some of these days for the same customer. End that rate first, or use a different code.',
  rate_in_use: () => 'This rate is the billing rate of a rent-in, so it has to stay a rented-in equipment rate.',
  currency_missing: () => "Set the company's currency (CAD or USD) in Settings before adding rates or drafting a ticket.",
  currency_not_settable: () => "A rate is in the company's currency; it is not set per rate.",
  crew_role_not_labour: () => 'A crew role applies to a labour rate only.',
  equipment_not_equipment_kind: () => 'A unit or equipment type applies to an owned-equipment rate only.',
  unit_and_type: () => 'A rate prices one unit or one equipment type, not both. Pick one.',
  rate_not_rent_in: () => 'A rent-in bills at a rented-in equipment rate. Pick a rate of that kind.',
  rate_other_customer: () => "That rate belongs to a different customer than this job's.",
  rate_other_currency: () => "That rate is in a different currency from this ticket's.",
  rate_unit_mismatch: () => "That rate is counted in a different unit from this line. Pick a rate with the line's unit.",
  unit_fixed: () => 'A line drafted from the logs keeps the unit its hours were counted in.',
  // drafting
  work_date_required: () => 'This job is ticketed per day. Pick the day to ticket.',
  work_date_not_per_day: () => 'This job is ticketed per job: one ticket covers every day, so no day is picked.',
  no_daily_logs: (x) => str(x.error) ?? 'There is no daily log to draft from. A ticket is drafted from the daily logs.',
  ticket_exists: () => 'This job already has a ticket for that. Open it from the list below.',
  ticket_scope_conflict: (x) =>
    str(x.error) ?? 'This job already has a ticket drafted the other way (per job or per day). A job is ticketed one way, so the work is not billed twice.',
  // editing and approving
  ticket_customer_approved: () => "The customer has approved this ticket, so what it bills can't change.",
  ticket_changed: () => 'This ticket changed while you were editing it. Reload it and try again.',
  ticket_transition_not_allowed: () => 'This ticket has already moved on. Reload it to see where it is.',
  ticket_unpriced: (x) => {
    const n = Array.isArray(x.lines) ? x.lines.length : 0;
    return n === 1
      ? 'One line has no rate. Price it or remove it before the ticket can be approved.'
      : `${n || 'Some'} lines have no rate. Price them or remove them before the ticket can be approved.`;
  },
  ticket_empty: () => 'This ticket has no lines. Refresh it from the daily logs or add a line before approving it.',
  tax_unconfigured: (x) => str(x.error) ?? 'Tax is not configured for where this work was done. Set it in Settings → Regional & Tax.',
  // sending
  ticket_not_office_approved: () => 'Approve the ticket as the office first.',
  no_customer_email: () => "This customer has no email address on file. Enter the company man's email to send the ticket.",
  email_invalid: () => 'Enter a valid email address.',
  cc_invalid: (x) => str(x.error) ?? 'One of the copy addresses is not a valid email.',
  email_send_failed: () => 'The email could not be sent. The review link below still works; copy it to the company man.',
  // the photo of the signed paper ticket / LEM
  approval_already_recorded: () => 'This ticket already carries a recorded customer approval. It cannot be replaced.',
  date_in_future: () => "The day it was signed can't be in the future.",
  date_before_office_approval: () => "The day it was signed can't be before the office approved the ticket.",
  invalid_upload: () => 'That upload is not a photo filed against this ticket. Attach the file again.',
  file_type_invalid: () => 'Attach a PDF, PNG or JPEG file.',
  file_required: () => 'Attach the photo of the signed ticket.',
  file_too_large: () => 'The file is larger than 20 MB.',
  approved_by_name_required: () => 'Enter who signed the ticket.',
  approved_on_required: () => 'Enter the day the ticket was signed.',
  text_too_long: () => 'That name is too long.',
};

export const PF_TICKET_REFUSAL_CODES = Object.keys(PF_TICKET_REFUSALS);

export function pfTicketRefusal(err: unknown, fallback: string): string {
  const e = pfApiError(err);
  if (e.code && PF_TICKET_REFUSALS[e.code]) return PF_TICKET_REFUSALS[e.code]({ ...e.extra, error: e.message });
  return pfRefusal(err, fallback);
}

/** The lines a `ticket_unpriced` refusal names (id + description). Empty for any other error. */
export function unpricedLinesFrom(err: unknown): { id: string; description: string }[] {
  const e = pfApiError(err);
  if (e.code !== 'ticket_unpriced' || !Array.isArray(e.extra.lines)) return [];
  return (e.extra.lines as unknown[])
    .filter((l): l is { id: string; description?: unknown } => !!l && typeof (l as { id?: unknown }).id === 'string')
    .map((l) => ({ id: l.id, description: typeof l.description === 'string' && l.description ? l.description : '—' }));
}

/** The review link a 502 `email_send_failed` still carries (data.reviewUrl). */
export function reviewUrlFrom(err: unknown): string | null {
  const d = pfApiError(err).extra.data as { reviewUrl?: unknown } | undefined;
  return d && typeof d.reviewUrl === 'string' && d.reviewUrl ? d.reviewUrl : null;
}

/** `$450.00/hour` style suffix for a rate's unit; distance names km or mi when known. */
export function pfRateUnitSuffix(unit: string | null | undefined, distanceUnit?: string | null): string {
  switch (unit) {
    case 'hour':
      return '/hr';
    case 'day':
      return '/day';
    case 'each':
      return ' each';
    case 'distance':
      return distanceUnit ? `/${distanceUnit}` : '/distance';
    case 'flat':
      return ' flat';
    default:
      return '';
  }
}
